import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Plus, Edit, Trash2, Loader2 } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { CashflowFormModal } from "./cashflow-form-modal";

// ============================================================================
// TYPES
// ============================================================================

interface CashflowProjection {
  id: string;
  securityPropertyId: string;
  projectionYear: number;
  periodStartDate: string;
  periodEndDate: string;
  grossRentalIncome: string;
  otherIncome: string | null;
  vacancyAllowance: string | null;
  councilRates: string | null;
  waterRates: string | null;
  insurance: string | null;
  landTax: string | null;
  bodyCorporate: string | null;
  managementFees: string | null;
  repairsMaintenance: string | null;
  otherOutgoings: string | null;
  capex: string | null;
  outgoingsGrowthRate: string | null;
  rentGrowthRate: string | null;
  interestRateAssumed: string | null;
  isAutoCalculated: boolean | null;
}

interface CashflowProjectionsPanelProps {
  securityId: string;
}

// ============================================================================
// HELPERS
// ============================================================================

const AUD = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function formatCurrency(value: string | null | undefined): string {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return AUD.format(num);
}

function toNum(value: string | null | undefined): number {
  if (!value) return 0;
  const n = parseFloat(value);
  return isNaN(n) ? 0 : n;
}

// Calculate derived/computed fields from a projection row
function calcEffectiveGrossIncome(p: CashflowProjection): number {
  return (
    toNum(p.grossRentalIncome) +
    toNum(p.otherIncome) -
    toNum(p.vacancyAllowance)
  );
}

function calcTotalOutgoings(p: CashflowProjection): number {
  return (
    toNum(p.councilRates) +
    toNum(p.waterRates) +
    toNum(p.insurance) +
    toNum(p.landTax) +
    toNum(p.bodyCorporate) +
    toNum(p.managementFees) +
    toNum(p.repairsMaintenance) +
    toNum(p.otherOutgoings)
  );
}

function calcNetOperatingIncome(p: CashflowProjection): number {
  return calcEffectiveGrossIncome(p) - calcTotalOutgoings(p);
}

// ============================================================================
// ROW DEFINITIONS
// ============================================================================

type RowDefinition = {
  label: string;
  testId: string;
  getValue: (p: CashflowProjection) => string;
  isCalculated?: boolean;
  isSectionHeader?: boolean;
};

const INCOME_ROWS: RowDefinition[] = [
  {
    label: "Gross Rental Income",
    testId: "row-gross-rental-income",
    getValue: (p) => formatCurrency(p.grossRentalIncome),
  },
  {
    label: "Other Income",
    testId: "row-other-income",
    getValue: (p) => formatCurrency(p.otherIncome),
  },
  {
    label: "Vacancy Allowance",
    testId: "row-vacancy-allowance",
    getValue: (p) => formatCurrency(p.vacancyAllowance),
  },
];

const EGI_ROW: RowDefinition = {
  label: "Effective Gross Income",
  testId: "row-effective-gross-income",
  getValue: (p) => AUD.format(calcEffectiveGrossIncome(p)),
  isCalculated: true,
};

const OUTGOING_ROWS: RowDefinition[] = [
  {
    label: "Council Rates",
    testId: "row-council-rates",
    getValue: (p) => formatCurrency(p.councilRates),
  },
  {
    label: "Water Rates",
    testId: "row-water-rates",
    getValue: (p) => formatCurrency(p.waterRates),
  },
  {
    label: "Insurance",
    testId: "row-insurance",
    getValue: (p) => formatCurrency(p.insurance),
  },
  {
    label: "Land Tax",
    testId: "row-land-tax",
    getValue: (p) => formatCurrency(p.landTax),
  },
  {
    label: "Body Corporate",
    testId: "row-body-corporate",
    getValue: (p) => formatCurrency(p.bodyCorporate),
  },
  {
    label: "Management Fees",
    testId: "row-management-fees",
    getValue: (p) => formatCurrency(p.managementFees),
  },
  {
    label: "Repairs & Maintenance",
    testId: "row-repairs-maintenance",
    getValue: (p) => formatCurrency(p.repairsMaintenance),
  },
  {
    label: "Other Outgoings",
    testId: "row-other-outgoings",
    getValue: (p) => formatCurrency(p.otherOutgoings),
  },
];

const TOTAL_OUTGOINGS_ROW: RowDefinition = {
  label: "Total Outgoings",
  testId: "row-total-outgoings",
  getValue: (p) => AUD.format(calcTotalOutgoings(p)),
  isCalculated: true,
};

const NOI_ROW: RowDefinition = {
  label: "Net Operating Income (NOI)",
  testId: "row-net-operating-income",
  getValue: (p) => AUD.format(calcNetOperatingIncome(p)),
  isCalculated: true,
};

const ASSUMPTION_ROWS: RowDefinition[] = [
  {
    label: "Rent Growth Rate (%)",
    testId: "row-rent-growth-rate",
    getValue: (p) => (p.rentGrowthRate ? `${p.rentGrowthRate}%` : "-"),
  },
  {
    label: "Outgoings Growth Rate (%)",
    testId: "row-outgoings-growth-rate",
    getValue: (p) => (p.outgoingsGrowthRate ? `${p.outgoingsGrowthRate}%` : "-"),
  },
];

// Ordered list of all rows for the spreadsheet grid
const ALL_ROWS: RowDefinition[] = [
  ...INCOME_ROWS,
  EGI_ROW,
  ...OUTGOING_ROWS,
  TOTAL_OUTGOINGS_ROW,
  NOI_ROW,
  ...ASSUMPTION_ROWS,
];

// ============================================================================
// COMPONENT
// ============================================================================

export function CashflowProjectionsPanel({ securityId }: CashflowProjectionsPanelProps) {
  const { toast } = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProjection, setEditingProjection] = useState<CashflowProjection | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    projection: CashflowProjection | null;
  }>({ open: false, projection: null });

  // Fetch cashflow projections
  const { data: projectionsData, isLoading } = useQuery<CashflowProjection[]>({
    queryKey: ["/api/crm/securities", securityId, "property", "cashflow"],
    queryFn: async () => {
      const response = await apiRequest(
        "GET",
        `/api/crm/securities/${securityId}/property/cashflow`
      );
      return response.json();
    },
    enabled: !!securityId,
  });

  const projections = Array.isArray(projectionsData) ? projectionsData : [];

  // Sort projections by year
  const sortedProjections = [...projections].sort(
    (a, b) => a.projectionYear - b.projectionYear
  );

  // Determine the next suggested year number
  const existingYears = new Set(sortedProjections.map((p) => p.projectionYear));
  let suggestedYear = 1;
  while (existingYears.has(suggestedYear)) {
    suggestedYear++;
  }

  // Chart data: NOI trend over years
  const chartData = useMemo(() => {
    return sortedProjections.map((p) => ({
      name: `Year ${p.projectionYear}`,
      noi: calcNetOperatingIncome(p),
      egi: calcEffectiveGrossIncome(p),
      outgoings: calcTotalOutgoings(p),
    }));
  }, [sortedProjections]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (projectionId: string) => {
      const response = await apiRequest(
        "DELETE",
        `/api/crm/securities/${securityId}/property/cashflow/${projectionId}`
      );
      if (!response.ok) {
        throw new Error("Failed to delete projection");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/crm/securities", securityId, "property", "cashflow"],
      });
      toast({ description: "Projection deleted" });
      setDeleteDialog({ open: false, projection: null });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleEdit = (projection: CashflowProjection) => {
    setEditingProjection(projection);
    setModalOpen(true);
  };

  const handleModalClose = (open: boolean) => {
    setModalOpen(open);
    if (!open) setEditingProjection(null);
  };

  const handleAddYear = () => {
    setEditingProjection(null);
    setModalOpen(true);
  };

  // Loading state
  if (isLoading) {
    return (
      <Card data-testid="cashflow-projections-panel">
        <CardHeader className="flex flex-row items-center justify-between py-3">
          <CardTitle className="text-base font-medium">Cashflow Projections (NOI)</CardTitle>
          <Button size="sm" variant="outline" disabled>
            <Plus className="h-4 w-4 mr-1" />
            Add Year
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-2" data-testid="loading-skeleton">
            <Skeleton className="h-8 w-full animate-pulse" />
            <Skeleton className="h-8 w-full animate-pulse" />
            <Skeleton className="h-8 w-full animate-pulse" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (sortedProjections.length === 0) {
    return (
      <>
        <Card data-testid="cashflow-projections-panel">
          <CardHeader className="flex flex-row items-center justify-between py-3">
            <CardTitle className="text-base font-medium">Cashflow Projections (NOI)</CardTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={handleAddYear}
              data-testid="button-add-year"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Year
            </Button>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-muted-foreground">
              No cashflow projections found
            </div>
          </CardContent>
        </Card>

        <CashflowFormModal
          open={modalOpen}
          onOpenChange={handleModalClose}
          securityId={securityId}
          existingProjection={editingProjection}
          suggestedYear={suggestedYear}
        />
      </>
    );
  }

  // Spreadsheet grid — rows = metrics, columns = years
  return (
    <>
      <Card data-testid="cashflow-projections-panel">
        <CardHeader className="flex flex-row items-center justify-between py-3">
          <CardTitle className="text-base font-medium">Cashflow Projections (NOI)</CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={handleAddYear}
            data-testid="button-add-year"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Year
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto" data-testid="cashflow-table">
            <Table>
              <TableHeader>
                <TableRow>
                  {/* Metric label column */}
                  <TableHead className="min-w-[200px] sticky left-0 bg-background z-10 border-r">
                    Metric
                  </TableHead>
                  {/* One column per projection year */}
                  {sortedProjections.map((p) => (
                    <TableHead
                      key={p.id}
                      className="min-w-[160px] text-right"
                      data-testid={`col-year-${p.projectionYear}`}
                    >
                      <div className="flex flex-col items-end gap-1">
                        <span>Year {p.projectionYear}</span>
                        <span className="text-xs font-normal text-muted-foreground">
                          {p.periodStartDate} – {p.periodEndDate}
                        </span>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            onClick={() => handleEdit(p)}
                            data-testid={`button-edit-${p.id}`}
                            aria-label={`Edit Year ${p.projectionYear}`}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                            onClick={() => setDeleteDialog({ open: true, projection: p })}
                            data-testid={`button-delete-${p.id}`}
                            aria-label={`Delete Year ${p.projectionYear}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {ALL_ROWS.map((row) => (
                  <TableRow
                    key={row.testId}
                    className={
                      row.isCalculated
                        ? "bg-muted/30 font-semibold"
                        : undefined
                    }
                    data-testid={row.testId}
                  >
                    {/* Metric name cell */}
                    <TableCell
                      className={[
                        "sticky left-0 bg-background z-10 border-r py-2 px-3 text-sm",
                        row.isCalculated ? "font-semibold bg-muted/30" : "",
                      ].join(" ")}
                    >
                      {row.label}
                    </TableCell>
                    {/* Value cells — one per year */}
                    {sortedProjections.map((p) => (
                      <TableCell
                        key={p.id}
                        className={[
                          "text-right py-2 px-3 text-sm tabular-nums",
                          row.isCalculated ? "font-semibold" : "",
                        ].join(" ")}
                        data-testid={`cell-${row.testId}-year-${p.projectionYear}`}
                      >
                        {row.getValue(p)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* NOI Trend Chart */}
      {sortedProjections.length >= 2 && (
        <Card className="mt-4" data-testid="chart-cashflow-projections">
          <CardHeader className="py-3">
            <CardTitle className="text-base font-medium">NOI Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value: number) =>
                      `$${(value / 1000).toFixed(0)}k`
                    }
                  />
                  <Tooltip
                    formatter={(value: number) =>
                      AUD.format(value)
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="noi"
                    name="NOI"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="egi"
                    name="EGI"
                    stroke="hsl(142, 76%, 36%)"
                    strokeWidth={1}
                    strokeDasharray="4 4"
                    dot={{ r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="outgoings"
                    name="Outgoings"
                    stroke="hsl(0, 84%, 60%)"
                    strokeWidth={1}
                    strokeDasharray="4 4"
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      <CashflowFormModal
        open={modalOpen}
        onOpenChange={handleModalClose}
        securityId={securityId}
        existingProjection={editingProjection}
        suggestedYear={suggestedYear}
      />

      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) =>
          setDeleteDialog({ open, projection: open ? deleteDialog.projection : null })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Cashflow Projection</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete Year{" "}
              {deleteDialog.projection?.projectionYear} projection? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                deleteDialog.projection &&
                deleteMutation.mutate(deleteDialog.projection.id)
              }
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
